// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------
import * as React from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IClaimOrder } from './../../businessLogic/sagas/serviceRequest/types';
import { logPageAction } from 'src/lib/ApplicationInsights';
import history from 'src/history';
import { getRepairOffer } from './../../store/orderCreation/actions';
import {
    Progress,
    updateCreateOrderProgress,
    saveUserDeviceAndAccessorySelection,
    zipCodeSelector,
    updateReactivateOrderStatus,
    getShippingAddressData,
    saveZipCodeData,
} from './../../store/orderCreation';
import { userAgentTierSelector, userSoldToIdSelector } from './../../store/user/selector';
import {
    putSelectedCountryData,
    selectedCountryInfoSelector
} from './../../store/countries';

import { deviceSelector, deviceEligibilitySelector } from './../../store/deviceInfo/selectors';
import { countriesSelector } from './../../components/ServiceRequestAndDeviceLookup/selector';
interface PropType {
    claim: IClaimOrder;
}

/*interface ListItemProps {
    name: string;
    sku: string;
    serialNumber: string;
}*/
export default function ReActivateLink ({ claim }: PropType) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    
    const device = useSelector(state => deviceSelector(state));
    const userAgent: string = useSelector(state => userAgentTierSelector(state));
    const soldToId: string = useSelector(state => userSoldToIdSelector(state));
    let { countryCode } = useSelector(state => selectedCountryInfoSelector(state));
    const allCountries = useSelector(state => countriesSelector(state));
    const [countryInfo, setCountryInfo] = React.useState({ countryCode: '', countryName: '', countryShortCode: '' });
    
    let zipCode: string = useSelector(state => zipCodeSelector(state));
        
    const eligibilityOptions = useSelector(state => deviceEligibilitySelector(state));
    const deviceItems = {};
    const isFullDeviceRepair = false;

    const findCountryDetails = (selectCountryCode:string) => {
        return selectCountryCode && Array.isArray(allCountries) && allCountries.find((country: any) => {
            return country
                && (
                    (country.ISOCode && country.ISOCode.toUpperCase().trim() === selectCountryCode.trim())
                    || (country.ISOCountryShortCode && country.ISOCountryShortCode.toUpperCase().trim() === selectCountryCode)
                );
        });
    };

    if(countryInfo.countryCode === ''){
        countryCode = claim.shipments.shipTo.country;
        const countryData = findCountryDetails(claim.shipments.shipTo.country);
        const selCountryData = { countryCode:countryData.ISOCode, countryName: countryData.ISOName, countryShortCode: countryData.ISOCountryShortCode };
        setCountryInfo(selCountryData);
        dispatch(putSelectedCountryData(selCountryData));
        if(zipCode === ''){
            zipCode = claim.shipments.shipTo.postalCode;
            dispatch(
                saveZipCodeData({
                    zipCode,
                })
            );
        }
    }

    

    function onClick (e: React.MouseEvent) {
        logPageAction('ReActivate link clicked');
        if (e && e.stopPropagation) e.stopPropagation();
        dispatch(
            updateReactivateOrderStatus(true)
        );
        /////////
        const lineItem = claim.lineItems[0];
        const selectedParent = {
            'name': lineItem.deviceInfo.localizedName,
            'serialNumber': lineItem.deviceInfo.serialNumber,
            'sku': lineItem.deviceInfo.partNumber,
        };
        const selectedAccessory:any = []; //temp
        
        dispatch(
            saveUserDeviceAndAccessorySelection({
                parent: selectedParent,
                accessory: selectedAccessory,
            })
        );
        /////////////
        const lineItems = claim.lineItems;

        lineItems.forEach((lineItem: any) => {
            if(lineItem?.status?.toLowerCase() !== "cancelled")
            {
                let accessoryType: any;
                const selectedProblemType = eligibilityOptions.find((option: any) => option.symptomCode === lineItem.symptomCode) ?? { symptomCode: lineItem.symptomCode };
                if (lineItem.accessoryType && lineItem.accessoryType !== 'SN') {
                    accessoryType = lineItem.accessoryType;
                } else if (lineItem.componentDeviceInfo) {
                    accessoryType = lineItem.componentDeviceInfo.serialNumberType;
                } else {
                    accessoryType = lineItem.deviceInfo.serialNumberType;
                }
                const partInfo = eligibilityOptions.find((option: any) => option.symptomCode === accessoryType);
                deviceItems[accessoryType] = {
                    ...partInfo,
                    numberOfRepairAttempts: undefined,
                    //onDemandFru: true,
                    problemDescription: lineItem.comments,
                    problemType: selectedProblemType,
                    repairDetails: '',
                };
            }
        });

        displayOffers();
    }

    const displayOffers = () => {
        const repairDetails: {} = {
            isFullDeviceRepair,
            deviceItems,
            accessories: {},
        };

        dispatch(getRepairOffer({
            repairDetails,
            additionalInfo: {
                userAgent,
                countryCode,
                soldToId,
                serialNumber: device.serialNumber,
                postalCode:zipCode,
                hasParentOnDemandOrder: true
            },
        }));

        //put the selected shipping address to redux store as shippingAddress to be used in the next action
        const shipToAddr:any = {...claim.shipments.shipTo};
        shipToAddr.address1 = shipToAddr.addressLine1;
        shipToAddr.address2 = shipToAddr.addressLine2;
        shipToAddr.email = shipToAddr.emailId;
        shipToAddr.stateOrProvince = shipToAddr.region;

        delete shipToAddr.addressLine1;
        delete shipToAddr.addressLine2;
        delete shipToAddr.emailId;
        delete shipToAddr.region;
        dispatch(getShippingAddressData({...shipToAddr, validation: undefined }));
        dispatch(
            updateCreateOrderProgress(Progress.RepairOfferSelection)
        );

        history.push('/device/createOrder', {} );
    };

    return (
        <div>
            <a href="javascript:;" onClick={onClick} aria-label={t('ReActivate_link')}>
                {t('ReActivate_link')}
            </a>
        </div>
    );
}